import { Capacitor } from '@capacitor/core';
import {
	AccessControl,
	BiometryType,
	NativeBiometric,
} from '@capgo/capacitor-native-biometric';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faEye,
	faFaceViewfinder,
	faFingerprint,
} from '@fortawesome/pro-solid-svg-icons';
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	useSyncExternalStore,
} from 'react';

// Re-exported so consumers can switch on the biometry type without a direct
// plugin dependency.
export { BiometryType };

/**
 * Biometric (Face ID / Touch ID / fingerprint) login support.
 *
 * Behavior on native iOS/Android Capacitor shells:
 *   1. After a successful credential login, call `enable({ email, refreshToken })`
 *      to persist the user's refresh token in secure storage (platform
 *      specifics below).
 *   2. On subsequent visits, call `tryAutoLogin()` when the user taps
 *      "Sign in with Face ID". A biometric match gates the stored token;
 *      on success the caller exchanges it at the OAuth refresh endpoint to
 *      skip the email/password screen entirely.
 *   3. `disable()` clears the stored token — wire it into Logout and into the
 *      refresh-token-rejected fallback path.
 *
 * On web (non-native): every method resolves to a "not available" result and
 * `tryAutoLogin()` returns `null` immediately, so components can render the
 * regular login form unconditionally and the biometric fast path never fires.
 *
 * Platform split for the token's at-rest gating (PD-14764):
 *   - iOS: the token is written behind a
 *     `SecAccessControl(.biometryCurrentSet)` keychain item. Writes are
 *     silent (`SecItemAdd`), the READ presents the OS Face ID prompt, and
 *     the OS auto-invalidates the item when enrolled biometrics change.
 *     Verified on-device 2026-07-14.
 *   - Android: the plugin presents a BiometricPrompt for access-controlled
 *     WRITES as well as reads (AuthActivity, mode=setSecureData) — so
 *     OS-gated storage cannot be kept in lockstep with silent token
 *     rotations the way the iOS keychain allows; every rotation sync popped
 *     a "Protect Data" prompt. Android therefore stores the token un-gated
 *     (Keystore-encrypted value in app-private SharedPreferences, silent
 *     writes) and `tryAutoLogin()` gates the read behind an explicit
 *     `verifyIdentity()` biometric prompt instead.
 *
 * Keychain isolation: each tenant-branded storefront app ships with its own
 * bundle id, so its keychain is already sandboxed from every other app. The
 * keys below can therefore be stable constants without an app-id namespace.
 */

// Holds the OAuth refresh token. iOS: gated behind a biometric match
// (OS-enforced). Android: un-gated at rest; the read is gated by an explicit
// verifyIdentity() in tryAutoLogin (see the platform-split note above).
const KEY_REFRESH_TOKEN = 'portal.biometric.refreshToken';
// Un-gated: the account email, used only to label the quick-sign-in button.
// Not a secret, and we want to display it without forcing a biometric prompt.
const KEY_EMAIL = 'portal.biometric.email';

// PD-15748 — set at EXPLICIT logout (and only then), read by the biometric
// gate to decide whether the next Face ID sign-in should offer the tenant
// chooser for multi-tenant users. Plain app relaunches never set it, so
// session restores keep going straight into the last tenant. Deliberately
// NOT `workd:`-prefixed: the logout flow wipes every `workd:` key, and this
// flag exists precisely to survive that wipe (same reasoning as the
// remembered receipt printer).
const KEY_CHOOSER_ON_NEXT_LOGIN = 'portal.biometric.chooserOnNextLogin';

/** Record that the user explicitly logged out (see KEY_CHOOSER_ON_NEXT_LOGIN). */
export function markBiometricLogoutForChooser(): void {
	try {
		localStorage.setItem(KEY_CHOOSER_ON_NEXT_LOGIN, '1');
	} catch {
		// Storage unavailable — the next login simply skips the chooser.
	}
}

/** Whether the next biometric sign-in should offer the tenant chooser. */
export function shouldOfferTenantChooser(): boolean {
	try {
		return localStorage.getItem(KEY_CHOOSER_ON_NEXT_LOGIN) === '1';
	} catch {
		return false;
	}
}

/** Clear the chooser flag — call after any successful biometric sign-in. */
export function clearTenantChooserFlag(): void {
	try {
		localStorage.removeItem(KEY_CHOOSER_ON_NEXT_LOGIN);
	} catch {
		// Nothing actionable.
	}
}

/** Lazy platform check so tests can mock Capacitor.getPlatform per case. */
function isAndroidPlatform(): boolean {
	return Capacitor.getPlatform() === 'android';
}

/**
 * Module-shared "a biometric session is stored" flag. The hook is mounted by
 * several surfaces at once (login modal, account page), and per-instance
 * state goes stale the moment another instance enables/disables enrollment —
 * the account page's turn-off left the login modal still offering a dead
 * "Sign in with Face ID" button. Every instance reads this shared value via
 * useSyncExternalStore, so an enable()/disable() anywhere updates every
 * surface at once.
 */
let storedSessionSnapshot = false;
const storedSessionListeners = new Set<() => void>();

function setStoredSessionShared(value: boolean): void {
	if (storedSessionSnapshot === value) {
		return;
	}
	storedSessionSnapshot = value;
	storedSessionListeners.forEach((listener) => {
		listener();
	});
}

function subscribeStoredSession(listener: () => void): () => void {
	storedSessionListeners.add(listener);
	return () => {
		storedSessionListeners.delete(listener);
	};
}

function getStoredSessionSnapshot(): boolean {
	return storedSessionSnapshot;
}

/**
 * Options for every Android verifyIdentity call (PD-14764). The plugin's
 * DEFAULT Android configuration requests strong+weak authenticators AND
 * binds a Keystore CryptoObject to the prompt — a combination androidx
 * rejects with an IllegalArgumentException ("Crypto-based authentication is
 * not supported for Class 2 (Weak) biometrics") that crashes the whole app
 * the moment the prompt activity launches, on every device. Restricting to
 * FINGERPRINT maps to the BIOMETRIC_STRONG authenticator (a class filter,
 * not a modality filter — Class 3 face unlock still qualifies), which is a
 * legal pairing with the plugin's crypto binding. Availability gating below
 * mirrors this: Android only offers the feature when a STRONG biometric is
 * enrolled.
 */
const ANDROID_VERIFY_OPTIONS = {
	allowedBiometryTypes: [BiometryType.FINGERPRINT],
	// Crypto-free prompt (our patches/@capgo__capacitor-native-biometric
	// patch adds this option): the identity check is a pure BiometricPrompt
	// with no Keystore CryptoObject bound. The plugin's crypto binding
	// depends on hardware auth tokens that some OEM keymasters and the
	// Android emulator deliver unreliably — it failed verification AFTER a
	// successful fingerprint match ("Biometric security check failed",
	// code 10). Our Android storage is app-logic-gated (not key-gated), so
	// the CryptoObject added no protection here anyway.
	skipCrypto: true,
};

/**
 * Persist the refresh token with the platform-appropriate gating — the ONE
 * place that decides gated-vs-ungated storage (PD-14764). Both forms write
 * silently on their platform; only Android's gated form would prompt, and
 * that is exactly the form this helper never uses.
 */
async function writeStoredToken(refreshToken: string): Promise<void> {
	if (isAndroidPlatform()) {
		await NativeBiometric.setData({
			key: KEY_REFRESH_TOKEN,
			value: refreshToken,
		});
		return;
	}
	await NativeBiometric.setData({
		key: KEY_REFRESH_TOKEN,
		value: refreshToken,
		accessControl: AccessControl.BIOMETRY_CURRENT_SET,
	});
}

/**
 * Silently sync the stored refresh token after a rotation.
 *
 * The OAuth refresh endpoint rotates the refresh token on every exchange
 * (a ~5-minute cadence while the app is open), so a token captured once at
 * enable-time is stale within minutes — biometric sign-in would then unlock
 * the keychain only to exchange a dead token (PD-14601). Token services call
 * this after every successful rotation; it writes only when the user has
 * biometric login enabled, and never prompts: on iOS a `BIOMETRY_CURRENT_SET`
 * keychain write is an un-gated `SecItemAdd` (the biometric gate protects
 * reads, not writes), and on Android the token is stored un-gated
 * (PD-14764 — the plugin's gated Android write would raise a BiometricPrompt
 * on every rotation, which is the repeated-prompt defect this split fixes).
 *
 * Android legacy note: an enrollment created before the un-gated switch has
 * its token in the plugin's gated store. This sync writes the un-gated key,
 * so any legacy user who is still signed in gets migrated silently on their
 * first rotation; tryAutoLogin handles the signed-out case.
 */
export async function updateStoredBiometricSession(
	refreshToken: string,
): Promise<void> {
	if (!Capacitor.isNativePlatform()) return;
	try {
		// Enrolled-check via the UN-gated email key (PD-14700): the two keys
		// are written/deleted together by enable()/disable(), so the email's
		// presence is a faithful signal — and unlike an existence query
		// against the biometry-gated token item, it can never trip the OS
		// authentication UI. Every keychain touch here (attribute check +
		// silent platform-appropriate write) is auth-free; the ONLY gated
		// operations in this module stay inside tryAutoLogin.
		const { isSaved } = await NativeBiometric.isDataSaved({
			key: KEY_EMAIL,
		});
		if (!isSaved) return;
		await writeStoredToken(refreshToken);
	} catch (e) {
		// No stored session yet, or a transient keychain error — nothing to
		// sync; the next explicit enable() rewrites everything.
		console.debug('[biometric] token sync skipped', e);
	}
}

/**
 * Existence check for a stored biometric session — attributes only, never
 * reads the gated value, so it never triggers a biometric prompt. Exposed
 * as a standalone async helper (not just hook state) for pre-render
 * decisions like the CRM app's pre-auth gate, where "is there a stored
 * session?" must be answered before choosing which screen to show.
 */
export async function hasStoredBiometricSession(): Promise<boolean> {
	if (!Capacitor.isNativePlatform()) {
		return false;
	}
	try {
		// Existence via the UN-gated email key (PD-14700) — enable()/disable()
		// write/delete both keys together, and probing the biometry-gated
		// token item (even attributes-only) risked tripping the OS auth UI on
		// device, which was one source of the doubled Face ID confirmation.
		const { isSaved } = await NativeBiometric.isDataSaved({
			key: KEY_EMAIL,
		});
		return isSaved;
	} catch {
		return false;
	}
}

/**
 * The account email stored alongside the biometric session. Deliberately
 * un-gated (it is not a secret) so sign-in screens can label the quick
 * sign-in button without forcing a biometric prompt.
 */
export async function getStoredBiometricEmail(): Promise<string | null> {
	if (!Capacitor.isNativePlatform()) {
		return null;
	}
	try {
		const { value } = await NativeBiometric.getData({ key: KEY_EMAIL });
		return value !== '' ? value : null;
	} catch {
		return null;
	}
}

export interface BiometricSession {
	email: string;
	refreshToken: string;
}

export interface BiometricCapability {
	/** True only when running inside a native Capacitor shell. */
	isNative: boolean;
	/** True when the device supports biometrics AND has at least one enrolled. */
	isAvailable: boolean;
	/** Friendly identifier for the biometric type ("Face ID", "Touch ID", etc.). */
	biometryName: string;
	biometryType: BiometryType;
}

/**
 * Detects platform + biometric availability without prompting the user.
 * Safe to call on every render; the underlying plugin call is idempotent.
 */
export function useBiometricCapability(): BiometricCapability {
	const [capability, setCapability] = useState<BiometricCapability>(() => ({
		isNative: Capacitor.isNativePlatform(),
		isAvailable: false,
		biometryName: 'Biometric',
		biometryType: BiometryType.NONE,
	}));

	useEffect(() => {
		if (!Capacitor.isNativePlatform()) return;

		void (async () => {
			try {
				// useFallback: false — only a real enrolled biometric counts;
				// a device passcode alone must not enable the feature, because
				// our stored token is gated on biometrics specifically.
				const info = await NativeBiometric.isAvailable({ useFallback: false });
				// Android additionally requires a Class 3 (STRONG) biometric —
				// the verify prompt is pinned to the BIOMETRIC_STRONG
				// authenticator (see ANDROID_VERIFY_OPTIONS), so a weak-only
				// device could never pass it; don't offer the feature there.
				// iOS Face ID / Touch ID are always strong, and its behavior
				// here is device-verified (PD-14601) — leave it untouched.
				const isAvailable = isAndroidPlatform()
					? info.isAvailable && info.strongBiometryIsAvailable
					: info.isAvailable;
				setCapability({
					isNative: true,
					isAvailable,
					biometryName: friendlyName(info.biometryType),
					biometryType: info.biometryType,
				});
			} catch (e) {
				// Plugin not registered or device error — treat as unavailable.
				console.debug('[biometric] isAvailable failed', e);
			}
		})();
	}, []);

	return capability;
}

interface BiometricLoginActions {
	/**
	 * Persist the user's session for biometric unlock. Call this AFTER a
	 * successful credential login. Prompts once to confirm the user's
	 * biometric (consent + hardware validation), then writes the refresh
	 * token behind OS-gated secure storage. Resolves true on success, false
	 * if the user declined the prompt or the device has no enrolled biometric.
	 */
	enable: (session: BiometricSession) => Promise<boolean>;

	/**
	 * Drop the stored session. Call from Logout AND when the OAuth refresh
	 * endpoint rejects the stored token (revoked, expired beyond limits,
	 * etc.) so the user falls back to credential login cleanly.
	 */
	disable: () => Promise<void>;

	/**
	 * Reads the stored refresh token, which makes the OS present the biometric
	 * prompt. On a successful match it returns the persisted session. Returns
	 * null when biometrics aren't enabled, the device doesn't support them, or
	 * the user cancels the prompt. Errors are swallowed so callers can fall
	 * through to the regular login screen.
	 */
	tryAutoLogin: (reason?: string) => Promise<BiometricSession | null>;

	/** True when a biometric session has been persisted on this device. */
	hasStoredSession: boolean;
}

export function useBiometricLogin(): BiometricLoginActions &
	BiometricCapability {
	const capability = useBiometricCapability();
	// Shared across every mounted instance (see setStoredSessionShared) so
	// enabling/disabling on one surface immediately updates the others.
	const hasStoredSession = useSyncExternalStore(
		subscribeStoredSession,
		getStoredSessionSnapshot,
		getStoredSessionSnapshot,
	);

	useEffect(() => {
		if (!capability.isNative) return;
		void (async () => {
			try {
				// Existence via the UN-gated email key (PD-14700) — see
				// hasStoredBiometricSession for why the gated token item is
				// never probed.
				const { isSaved } = await NativeBiometric.isDataSaved({
					key: KEY_EMAIL,
				});
				setStoredSessionShared(isSaved);
			} catch {
				setStoredSessionShared(false);
			}
		})();
	}, [capability.isNative]);

	const enable = useCallback(
		async ({ email, refreshToken }: BiometricSession): Promise<boolean> => {
			if (!capability.isAvailable) return false;
			try {
				// Consent + hardware validation. Rejects if the user cancels or
				// no biometric matches, in which case we leave nothing stored.
				// This is the single enrollment prompt on BOTH platforms — the
				// token write below is silent everywhere (PD-14764).
				await NativeBiometric.verifyIdentity({
					reason: 'Enable quick sign-in with biometrics',
					useFallback: false,
					...(isAndroidPlatform() ? ANDROID_VERIFY_OPTIONS : {}),
				});
				// Token: platform-appropriate silent write (iOS gated at rest,
				// Android un-gated — see the platform-split note at the top).
				await writeStoredToken(refreshToken);
				// Email: un-gated, so the quick-sign-in button can show the
				// account without a prompt.
				await NativeBiometric.setData({ key: KEY_EMAIL, value: email });
				setStoredSessionShared(true);
				return true;
			} catch (e) {
				console.debug('[biometric] enable rejected', e);
				return false;
			}
		},
		[capability.isAvailable],
	);

	const disable = useCallback(async (): Promise<void> => {
		if (!capability.isNative) return;
		try {
			await NativeBiometric.deleteData({ key: KEY_REFRESH_TOKEN });
			await NativeBiometric.deleteData({ key: KEY_EMAIL });
		} catch (e) {
			// Plugin error or keys missing — either way, we're "disabled".
			console.debug('[biometric] disable cleanup partial', e);
		}
		setStoredSessionShared(false);
	}, [capability.isNative]);

	const tryAutoLogin = useCallback(
		async (
			reason = 'Sign in to your account',
		): Promise<BiometricSession | null> => {
			if (!capability.isNative || !capability.isAvailable) return null;

			try {
				let refreshToken: string;
				if (isAndroidPlatform()) {
					// Android (PD-14764): the biometric gate is an explicit
					// verifyIdentity() prompt, then an un-gated read. A cancel
					// or failed match rejects here and falls through to null.
					await NativeBiometric.verifyIdentity({
						reason,
						useFallback: false,
						...ANDROID_VERIFY_OPTIONS,
					});
					try {
						({ value: refreshToken } = await NativeBiometric.getData({
							key: KEY_REFRESH_TOKEN,
						}));
					} catch (readError) {
						// No un-gated token: this is an enrollment from before
						// the un-gated storage switch, with its token stuck in
						// the plugin's gated store (readable only via another
						// prompt — every time). Signed-in legacy users are
						// migrated silently by the rotation sync before they
						// ever get here; for this signed-out one, clear the
						// stale enrollment so the next credential login can
						// re-enroll cleanly (one-time fallback).
						console.debug(
							'[biometric] no un-gated token — clearing legacy enrollment',
							readError,
						);
						await disable();
						return null;
					}
				} else {
					// iOS: go straight to the gated read — no existence precheck
					// against the protected item (PD-14700: probing it, even
					// attributes-only, risked an extra OS auth evaluation on
					// device, doubling the Face ID confirmation). A missing item
					// rejects with the plugin's not-found error and falls
					// through to null exactly like the old precheck did.
					//
					// The gated read presents the OS biometric prompt. A cancel
					// or failed match rejects here and we fall through to null.
					({ value: refreshToken } = await NativeBiometric.getSecureData({
						key: KEY_REFRESH_TOKEN,
						reason,
					}));
				}
				if (refreshToken === '') {
					await disable();
					return null;
				}

				// Email is best-effort labeling; never block sign-in on it.
				let email = '';
				try {
					const stored = await NativeBiometric.getData({ key: KEY_EMAIL });
					email = stored.value;
				} catch {
					// No stored email — proceed with the token alone.
				}
				return { email, refreshToken };
			} catch (e) {
				// User cancelled, biometry locked out, item invalidated, or a
				// plugin error — let the caller fall through to credential login.
				const code = (e as { code?: unknown }).code;
				if (code === '21' || code === 21) {
					// The plugin's not-found code: the stored item is gone (for
					// example, cleared from another surface while this one held
					// a stale enrolled state). Clear the leftover marker keys so
					// every surface converges on the un-enrolled UI instead of
					// offering a button that does nothing.
					await disable();
				}
				console.debug('[biometric] auto-login cancelled or failed', e);
				return null;
			}
		},
		[capability.isAvailable, capability.isNative, disable],
	);

	// Memoized: consumers put this object in effect dependency arrays, and a
	// fresh identity per render made those effects refire on every state
	// change — the cause of the repeated Face ID prompt loop during enrollment
	// (PD-14601).
	return useMemo(
		() => ({
			...capability,
			hasStoredSession,
			enable,
			disable,
			tryAutoLogin,
		}),
		[capability, hasStoredSession, enable, disable, tryAutoLogin],
	);
}

function friendlyName(type: BiometryType): string {
	switch (type) {
		case BiometryType.FACE_ID:
			return 'Face ID';
		case BiometryType.TOUCH_ID:
			return 'Touch ID';
		case BiometryType.FINGERPRINT:
			return 'Fingerprint';
		case BiometryType.FACE_AUTHENTICATION:
			return 'Face Authentication';
		case BiometryType.IRIS_AUTHENTICATION:
			return 'Iris';
		default:
			return 'Biometric';
	}
}

/**
 * Icon matching the device's biometry type. A fingerprint glyph next to a
 * "Sign in with Face ID" label reads wrong (PD-14659 review feedback) — face
 * authentication gets the face-scan glyph; the fingerprint stays only for
 * fingerprint-style sensors, and doubles as the generic-biometric fallback.
 */
export function biometricIconFor(type: BiometryType): IconDefinition {
	switch (type) {
		case BiometryType.FACE_ID:
		case BiometryType.FACE_AUTHENTICATION:
			return faFaceViewfinder;
		case BiometryType.IRIS_AUTHENTICATION:
			return faEye;
		default:
			return faFingerprint;
	}
}
